import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCalendarAlt, FaChevronLeft } from 'react-icons/fa'

import { Box, Button, Dialog, Grid, Tooltip, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'

import { useGET_groupCourseSessionAttendanceQuery, usePOST_groupCourseSessionAttendanceMutation } from 'redux/api/sessionsApi'
import { useGET_studentsQuery } from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SessionAttendanceItemList from 'components/molecules/SessionAttendanceItemList'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { validationSchema } from 'utils/formModels/sessionAttendanceModel'
import { crudColorMap, crudIconMap, formatDateTime, parseDateTime}  from 'utils/helpers'

import { ISession } from 'types'
import { format } from 'date-fns'

interface IProps {
  session: ISession
  viewOnly: boolean
  tabText?: string
  tabKey?: string
}


const SessionAttendanceForm: FC<IProps> = ({ session, viewOnly }) => {
  const { t } = useTranslation()

  const [saveAttendance, {isLoading, isSuccess, error}] = usePOST_groupCourseSessionAttendanceMutation()
  useErrorAlert(error)
  useSuccessAlert(isSuccess ? `${t('alerts.entity-saved', {entity: 'Attendance'})}` : '')

  const [showList, setShowList] = useState(true)

  const { items } = formModel()
  const action = 'edit'

  let filtersStudents = {
    group_course: session.group_course.id,
    page: 1,
    page_size: 999
  }

  const { data: studentsData, refetch: refetchStudents, isLoading: loadingStudents } = useGET_studentsQuery(filtersStudents, {skip: !session})
  const { data: attendanceData, refetch: refetchData, isLoading: loadingData } = useGET_groupCourseSessionAttendanceQuery(session.id, {skip: !session})
  
  const isReady = !(isLoading || loadingStudents || loadingData) && showList

  useEffect(() => {
    if (session.id) {
      setShowList(false)
      refetchStudents()
      refetchData()

      setTimeout(() => {
        setShowList(true)
      },200)

    }
  }, [session.id])
  
  const title = session && session?.group && session?.course ? `${format(parseDateTime(session?.start_datetime), 'dd-MM-yyyy HH:ii')} - ${t('common.group')} ${session?.group} - ${session?.course}` : ''
  
  return ( <>
    { studentsData && attendanceData &&
        <Formik
          enableReinitialize
          initialValues={
            { 
              items: 
                studentsData.results.map(student => { 
                  return {
                    student_id: student.student_profile_id,
                    student_academic_id: student.academic_id,
                    student_avatar: student.avatar,
                    student_groups: student.student_groups,
                    student_name: student.full_name,
                    ...attendanceData.results.map(item => {
                      return {
                        ...item,
                        status: {
                          id: item.status.id,
                          title: t(`attendance.status-${item.status.id}`)
                        }
                      }
                    }).find(item => item.student_id === student.student_profile_id)
                  }
                }) 
            }
          }
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const data = values.items.filter(item => item.status?.id).map(item => { 
              
              let saveData = {
                student_id: item.student_id,
                status: item.status?.id ?? null,
                participation_points: 0,
                absence_minutes: 0,
                comment: item.comment
              }

              if(item.status?.id !== 'present'){
                saveData["absence_minutes"]=item.absence_minutes
              }

              return saveData
            })

            saveAttendance({id:session.id, body: data});
          }}
        >
          <Form>

            <Grid container spacing={4}>

              <Grid item xs={12} lg={12}>
                {isReady && <SessionAttendanceItemList fieldProps={{ ...items }} title={title} />}
              </Grid>    

              <Grid item xs={12}>
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
    }
    </>
  )
}
export default SessionAttendanceForm
